import {
  normalizeGigProfileFormValues,
  normalizePreferredContactMethod,
} from '../../utils/gigProfileFields';

const DEFAULT_GIG_DURATION_MS = 3 * 60 * 60 * 1000;
export const MAX_GIG_HIGHLIGHT_PHOTOS = 24;
const DAY_KEYS = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

function toBoolean(value, fallback = false) {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value !== 0;
  const normalized = String(value || '').trim().toLowerCase();
  if (!normalized) return fallback;
  return ['1', 'true', 'yes', 'on'].includes(normalized);
}

function getToken() {
  return typeof window !== 'undefined' ? localStorage.getItem('token') : null;
}

function buildHeaders(extraHeaders = {}) {
  const token = getToken();
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...extraHeaders,
  };
}

async function gigApiFetch(path, options = {}) {
  const normalizedPath = path.startsWith('/api/') ? path : `/api${path}`;
  const response = await fetch(normalizedPath, {
    ...options,
    headers: buildHeaders(options.headers || {}),
  });

  if (!response.ok) {
    const text = await response.text();
    try {
      const parsed = JSON.parse(text);
      throw new Error(parsed.message || 'Gig request failed');
    } catch (error) {
      if (error instanceof SyntaxError) {
        throw new Error(text || 'Gig request failed');
      }
      throw error;
    }
  }

  return response.json();
}

function toDateInputValue(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const tzOffset = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - tzOffset).toISOString().slice(0, 16);
}

function toIsoOrNull(value) {
  if (!value) return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed.toISOString();
}

function toTimeInputValue(value) {
  if (!value) return '';
  const normalized = String(value).trim();
  const match = normalized.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
  if (!match) return '';
  return `${String(Number(match[1])).padStart(2, '0')}:${match[2]}`;
}

function isHistoricalPerformerStatus(status) {
  return ['replaced', 'cancelled'].includes(String(status || '').toLowerCase());
}

function derivePerformerNames(item = {}) {
  if (Array.isArray(item.performerNames) && item.performerNames.length) {
    return item.performerNames
      .map((entry) => String(entry || '').trim())
      .filter(Boolean);
  }

  return (Array.isArray(item.performers) ? item.performers : [])
    .filter((performer) => !isHistoricalPerformerStatus(performer?.status || performer?.performer_status))
    .map((performer) => String(performer?.displayName || performer?.display_name || '').trim())
    .filter(Boolean);
}

function buildPerformerSummary(item = {}, { maxNames = null } = {}) {
  const explicitSummary = String(item.performerSummary || item.performer_summary || '').trim();
  if (explicitSummary) return explicitSummary;

  const names = derivePerformerNames(item);
  if (!names.length) return '';
  if (maxNames && names.length > maxNames) {
    return `${names.slice(0, maxNames).join(' • ')} +${names.length - maxNames} more`;
  }
  return names.join(' • ');
}

function defaultPerformerForm() {
  return {
    id: null,
    artistId: null,
    displayName: '',
    setStartTime: '',
    setEndTime: '',
    instagramUrl: '',
    facebookUrl: '',
    twitterUrl: '',
    pinterestUrl: '',
    appleMusicUrl: '',
    mixcloudUrl: '',
    tiktokUrl: '',
    bio: '',
    genresText: '',
    soundcloudUrl: '',
    spotifyUrl: '',
    youtubeUrl: '',
    contactEmail: '',
    contactPhone: '',
    preferredContactMethod: '',
    preferredContactDetails: '',
    storeProfile: true,
    allowContactSharing: false,
    artworkCreativeId: '',
    artworkThumbnailUrl: null,
    submissionToken: null,
    inviteChannel: '',
    inviteTarget: '',
    inviteSentAt: null,
    confirmed: false,
    status: 'awaiting_submission',
    replacedByPerformerId: null,
    replacedAt: null,
    replacementReason: '',
    cancelledAt: null,
    cancellationReason: '',
    submittedAt: null,
  };
}

function buildCreativePreviewUrl(creativeId) {
  const normalizedId = Number(creativeId || 0);
  return Number.isInteger(normalizedId) && normalizedId > 0
    ? `/api/creative/assets/${normalizedId}/files/preview`
    : null;
}

function buildTitleFromFilename(filename = '') {
  const baseName = String(filename || '')
    .replace(/\.[^.]+$/, '')
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  return baseName || 'Gig highlight';
}

export function normalizeGigHighlightPhotos(items = []) {
  return (Array.isArray(items) ? items : [])
    .filter((item) => item && Number(item.creativeId || item.creative_id || 0) > 0)
    .map((item, index) => ({
      id: item.id == null ? null : Number(item.id),
      creativeId: Number(item.creativeId ?? item.creative_id),
      previewUrl:
        item.previewUrl
        || item.preview_url
        || buildCreativePreviewUrl(item.creativeId ?? item.creative_id),
      sortOrder: Number.isInteger(Number(item.sortOrder ?? item.sort_order))
        ? Number(item.sortOrder ?? item.sort_order)
        : index * 10,
    }))
    .sort((a, b) => a.sortOrder - b.sortOrder || a.creativeId - b.creativeId);
}

export function normalizeArtistItems(items = []) {
  return (Array.isArray(items) ? items : [])
    .filter((item) => item && Number(item.id) > 0)
    .map((item) => ({
      id: Number(item.id),
      siteId: Number(item.siteId || item.site_id || 0) || null,
      name: String(item.name || '').trim() || 'Untitled artist',
      instagramUrl: String(item.instagramUrl || item.instagram_url || '').trim(),
      facebookUrl: String(item.facebookUrl || item.facebook_url || '').trim(),
      twitterUrl: String(item.twitterUrl || item.twitter_url || '').trim(),
      pinterestUrl: String(item.pinterestUrl || item.pinterest_url || '').trim(),
      appleMusicUrl: String(item.appleMusicUrl || item.apple_music_url || '').trim(),
      mixcloudUrl: String(item.mixcloudUrl || item.mixcloud_url || '').trim(),
      tiktokUrl: String(item.tiktokUrl || item.tiktok_url || '').trim(),
      bio: String(item.bio || '').trim(),
      genres: Array.isArray(item.genres) ? item.genres.filter(Boolean).map((entry) => String(entry).trim()) : [],
      soundcloudUrl: String(item.soundcloudUrl || item.soundcloud_url || '').trim(),
      spotifyUrl: String(item.spotifyUrl || item.spotify_url || '').trim(),
      youtubeUrl: String(item.youtubeUrl || item.youtube_url || '').trim(),
      artworkCreativeId:
        item.artworkCreativeId == null && item.artwork_creative_id == null
          ? null
          : Number(item.artworkCreativeId ?? item.artwork_creative_id),
      artworkThumbnailUrl: item.artworkThumbnailUrl || null,
      email: String(item.email || '').trim(),
      phone: String(item.phone || '').trim(),
      preferredContactMethod: normalizePreferredContactMethod(item.preferredContactMethod || item.preferred_contact_method),
      preferredContactDetails: String(item.preferredContactDetails || item.preferred_contact_details || '').trim(),
      allowContactSharing: !!(item.allowContactSharing ?? item.allow_contact_sharing),
    }))
    .sort((a, b) => a.name.localeCompare(b.name) || a.id - b.id);
}

export function artistToPerformerFormPatch(artist) {
  if (!artist) return {};
  return {
    artistId: Number(artist.id) || null,
    displayName: artist.name || '',
    instagramUrl: artist.instagramUrl || '',
    facebookUrl: artist.facebookUrl || '',
    twitterUrl: artist.twitterUrl || '',
    pinterestUrl: artist.pinterestUrl || '',
    appleMusicUrl: artist.appleMusicUrl || '',
    mixcloudUrl: artist.mixcloudUrl || '',
    tiktokUrl: artist.tiktokUrl || '',
    bio: artist.bio || '',
    genresText: Array.isArray(artist.genres) ? artist.genres.join(', ') : '',
    soundcloudUrl: artist.soundcloudUrl || '',
    spotifyUrl: artist.spotifyUrl || '',
    youtubeUrl: artist.youtubeUrl || '',
    contactEmail: artist.email || '',
    contactPhone: artist.phone || '',
    preferredContactMethod: artist.preferredContactMethod || '',
    preferredContactDetails: artist.preferredContactDetails || '',
    storeProfile: true,
    allowContactSharing: !!artist.allowContactSharing,
    artworkCreativeId:
      artist.artworkCreativeId == null ? '' : String(artist.artworkCreativeId),
    artworkThumbnailUrl: artist.artworkThumbnailUrl || null,
  };
}

export function buildArtistPayload(formOrPerformer, { siteId = null } = {}) {
  const performer = formOrPerformer?.performer || formOrPerformer || defaultPerformerForm();
  const resolvedSiteId = Number(siteId || formOrPerformer?.siteId) || null;
  const normalized = normalizeGigProfileFormValues(performer);
  return {
    siteId: resolvedSiteId,
    name: String(performer.displayName || '').trim(),
    instagramUrl: normalized.instagramUrl,
    facebookUrl: normalized.facebookUrl,
    twitterUrl: normalized.twitterUrl,
    pinterestUrl: normalized.pinterestUrl,
    appleMusicUrl: normalized.appleMusicUrl,
    mixcloudUrl: normalized.mixcloudUrl,
    tiktokUrl: normalized.tiktokUrl,
    bio: String(performer.bio || '').trim(),
    genres: String(performer.genresText || '')
      .split(',')
      .map((entry) => entry.trim())
      .filter(Boolean),
    soundcloudUrl: normalized.soundcloudUrl,
    spotifyUrl: normalized.spotifyUrl,
    youtubeUrl: normalized.youtubeUrl,
    email: normalized.contactEmail,
    phone: normalized.contactPhone,
    preferredContactMethod: normalized.preferredContactMethod,
    preferredContactDetails: normalized.preferredContactDetails,
    allowContactSharing: normalized.allowContactSharing,
    storeProfile: normalized.storeProfile,
    artworkCreativeId:
      performer.artworkCreativeId === '' || performer.artworkCreativeId == null
        ? null
        : Number(performer.artworkCreativeId),
  };
}

export function normalizeGigTimelineItems(items = []) {
  return (Array.isArray(items) ? items : [])
    .filter((item) => item && Number.isInteger(Number(item.id)) && item.startsAtUtc && item.endsAtUtc)
    .map((item) => {
      const performerNames = derivePerformerNames(item);
      return {
        id: Number(item.id),
        siteId: Number(item.siteId || item.site_id || 0) || null,
        recurringTemplateId:
          item.recurringTemplateId == null && item.recurring_template_id == null
            ? null
            : Number(item.recurringTemplateId ?? item.recurring_template_id),
        title: String(item.title || '').trim() || 'Untitled gig',
        slug: String(item.slug || '').trim(),
        startsAtUtc: toIsoOrNull(item.startsAtUtc || item.starts_at_utc),
        endsAtUtc: toIsoOrNull(item.endsAtUtc || item.ends_at_utc),
        timezoneName: item.timezoneName || item.timezone_name || null,
        utcOffsetMinutesSnapshot:
          item.utcOffsetMinutesSnapshot == null && item.utc_offset_minutes_snapshot == null
            ? null
            : Number(item.utcOffsetMinutesSnapshot ?? item.utc_offset_minutes_snapshot),
        status: String(item.status || 'draft').toLowerCase(),
        performerSummary: buildPerformerSummary(item, { maxNames: 2 }),
        performerNames,
        artworkThumbnailUrl: item.artworkThumbnailUrl || null,
      };
    })
    .filter((item) => item.startsAtUtc && item.endsAtUtc)
    .sort((a, b) => {
      const startDelta = new Date(a.startsAtUtc).getTime() - new Date(b.startsAtUtc).getTime();
      if (startDelta !== 0) return startDelta;
      return a.id - b.id;
    });
}

export function normalizeGigItems(items = []) {
  return (Array.isArray(items) ? items : [])
    .filter((item) => item && Number(item.id) > 0)
    .map((item) => {
      const performerNames = derivePerformerNames(item);
      const gigArtworkCreativeId =
        item.gigArtworkCreativeId
        ?? item.gig_artwork_creative_id
        ?? item.artworkCreativeId
        ?? item.artwork_creative_id;
      return {
        ...item,
        id: Number(item.id),
        siteId: Number(item.siteId || item.site_id || 0) || null,
        recurringTemplateId:
          item.recurringTemplateId == null && item.recurring_template_id == null
            ? null
            : Number(item.recurringTemplateId ?? item.recurring_template_id),
        recurringOccurrenceDate: item.recurringOccurrenceDate || item.recurring_occurrence_date || null,
        startsAtUtc: toIsoOrNull(item.startsAtUtc || item.starts_at_utc),
        endsAtUtc: toIsoOrNull(item.endsAtUtc || item.ends_at_utc),
        gigArtworkCreativeId:
          gigArtworkCreativeId == null || Number(gigArtworkCreativeId) <= 0
            ? null
            : Number(gigArtworkCreativeId),
        gigArtworkThumbnailUrl: item.gigArtworkThumbnailUrl || item.artworkThumbnailUrl || null,
        performerSummary: buildPerformerSummary(item, { maxNames: 2 }),
        performerNames,
        highlightPhotos: normalizeGigHighlightPhotos(item.highlightPhotos || item.highlight_photos || []),
        performers: Array.isArray(item.performers)
          ? item.performers.map((performer) => performerToFormState(performer))
          : [],
      };
    })
    .sort((a, b) => {
      const leftStart = new Date(a.startsAtUtc || 0).getTime();
      const rightStart = new Date(b.startsAtUtc || 0).getTime();
      return leftStart - rightStart || a.id - b.id;
    });
}

export function createEmptyGigForm({
  siteId,
  startsAtUtc = null,
  endsAtUtc = null,
  timezoneName = null,
  utcOffsetMinutesSnapshot = null,
} = {}) {
  const baseStart = startsAtUtc ? new Date(startsAtUtc) : new Date();
  const baseEnd = endsAtUtc ? new Date(endsAtUtc) : new Date(baseStart.getTime() + DEFAULT_GIG_DURATION_MS);
  const resolvedTimeZone =
    timezoneName ||
    (typeof Intl !== 'undefined' ? Intl.DateTimeFormat().resolvedOptions().timeZone : null) ||
    'UTC';

  return {
    id: null,
    siteId: Number(siteId) || null,
    title: '',
    slug: '',
    description: '',
    gigArtworkCreativeId: '',
    gigArtworkThumbnailUrl: null,
    startsAtLocal: toDateInputValue(baseStart),
    endsAtLocal: toDateInputValue(baseEnd),
    timezoneName: resolvedTimeZone,
    utcOffsetMinutesSnapshot:
      utcOffsetMinutesSnapshot == null ? -baseStart.getTimezoneOffset() : Number(utcOffsetMinutesSnapshot),
    status: 'draft',
    highlightPhotos: [],
    performers: [defaultPerformerForm()],
  };
}

function performerToFormState(performer) {
  return {
    id: performer?.id || null,
    artistId: performer?.artistId == null ? null : Number(performer.artistId),
    displayName: performer?.displayName || '',
    setStartTime: toTimeInputValue(performer?.setStartTime || performer?.set_start_time),
    setEndTime: toTimeInputValue(performer?.setEndTime || performer?.set_end_time),
    instagramUrl: performer?.instagramUrl || '',
    facebookUrl: performer?.facebookUrl || performer?.facebook_url || '',
    twitterUrl: performer?.twitterUrl || performer?.twitter_url || '',
    pinterestUrl: performer?.pinterestUrl || performer?.pinterest_url || '',
    appleMusicUrl: performer?.appleMusicUrl || performer?.apple_music_url || '',
    mixcloudUrl: performer?.mixcloudUrl || performer?.mixcloud_url || '',
    tiktokUrl: performer?.tiktokUrl || performer?.tiktok_url || '',
    bio: performer?.bio || '',
    genresText: Array.isArray(performer?.genres) ? performer.genres.join(', ') : '',
    soundcloudUrl: performer?.soundcloudUrl || '',
    spotifyUrl: performer?.spotifyUrl || '',
    youtubeUrl: performer?.youtubeUrl || '',
    contactEmail: performer?.contactEmail || performer?.contact_email || '',
    contactPhone: performer?.contactPhone || performer?.contact_phone || '',
    preferredContactMethod:
      normalizePreferredContactMethod(performer?.preferredContactMethod || performer?.preferred_contact_method),
    preferredContactDetails: performer?.preferredContactDetails || performer?.preferred_contact_details || '',
    storeProfile: performer?.storeProfile == null && performer?.store_profile == null
      ? true
      : toBoolean(performer?.storeProfile ?? performer?.store_profile, true),
    allowContactSharing: toBoolean(performer?.allowContactSharing ?? performer?.allow_contact_sharing, false),
    artworkCreativeId:
      performer?.artworkCreativeId == null ? '' : String(performer.artworkCreativeId),
    artworkThumbnailUrl: performer?.artworkThumbnailUrl || null,
    submissionToken: performer?.submissionToken || null,
    inviteChannel: performer?.inviteChannel || '',
    inviteTarget: performer?.inviteTarget || '',
    inviteSentAt: performer?.inviteSentAt || null,
    confirmed: !!performer?.confirmed,
    status: performer?.status || 'awaiting_submission',
    replacedByPerformerId:
      performer?.replacedByPerformerId == null ? null : Number(performer.replacedByPerformerId),
    replacedAt: performer?.replacedAt || null,
    replacementReason: performer?.replacementReason || '',
    cancelledAt: performer?.cancelledAt || null,
    cancellationReason: performer?.cancellationReason || '',
    submittedAt: performer?.submittedAt || null,
  };
}

export function gigToFormState(gig) {
  const performers = Array.isArray(gig?.performers) && gig.performers.length
    ? gig.performers.map((performer) => performerToFormState(performer))
    : [defaultPerformerForm()];
  return {
    id: Number(gig?.id) || null,
    siteId: Number(gig?.siteId || gig?.site_id || 0) || null,
    title: gig?.title || '',
    slug: gig?.slug || '',
    description: gig?.description || '',
    gigArtworkCreativeId:
      gig?.gigArtworkCreativeId == null
      && gig?.gig_artwork_creative_id == null
      && gig?.artworkCreativeId == null
      && gig?.artwork_creative_id == null
        ? ''
        : String(gig?.gigArtworkCreativeId ?? gig?.gig_artwork_creative_id ?? gig?.artworkCreativeId ?? gig?.artwork_creative_id),
    gigArtworkThumbnailUrl: gig?.gigArtworkThumbnailUrl || gig?.artworkThumbnailUrl || null,
    startsAtLocal: toDateInputValue(gig?.startsAtUtc || gig?.starts_at_utc),
    endsAtLocal: toDateInputValue(gig?.endsAtUtc || gig?.ends_at_utc),
    timezoneName: gig?.timezoneName || gig?.timezone_name || '',
    utcOffsetMinutesSnapshot:
      gig?.utcOffsetMinutesSnapshot == null && gig?.utc_offset_minutes_snapshot == null
        ? ''
        : String(gig?.utcOffsetMinutesSnapshot ?? gig?.utc_offset_minutes_snapshot),
    status: gig?.status || 'draft',
    highlightPhotos: normalizeGigHighlightPhotos(gig?.highlightPhotos || gig?.highlight_photos || []),
    performers,
  };
}

function buildHighlightPhotoPayload(item, index) {
  return {
    ...(item?.id ? { id: Number(item.id) } : {}),
    creativeId: Number(item?.creativeId),
    sortOrder: Number.isInteger(Number(item?.sortOrder)) ? Number(item.sortOrder) : index * 10,
  };
}

function buildPerformerPayload(performer, { includeStatus = false } = {}) {
  const normalized = normalizeGigProfileFormValues(performer);
  const payload = {
    ...(performer?.id ? { id: performer.id } : {}),
    ...(performer?.artistId ? { artistId: Number(performer.artistId) } : {}),
    displayName: String(performer?.displayName || '').trim(),
    setStartTime: String(performer?.setStartTime || '').trim(),
    setEndTime: String(performer?.setEndTime || '').trim(),
    instagramUrl: normalized.instagramUrl,
    facebookUrl: normalized.facebookUrl,
    twitterUrl: normalized.twitterUrl,
    pinterestUrl: normalized.pinterestUrl,
    appleMusicUrl: normalized.appleMusicUrl,
    mixcloudUrl: normalized.mixcloudUrl,
    tiktokUrl: normalized.tiktokUrl,
    bio: String(performer?.bio || '').trim(),
    genres: String(performer?.genresText || '')
      .split(',')
      .map((entry) => entry.trim())
      .filter(Boolean),
    soundcloudUrl: normalized.soundcloudUrl,
    spotifyUrl: normalized.spotifyUrl,
    youtubeUrl: normalized.youtubeUrl,
    contactEmail: normalized.contactEmail,
    contactPhone: normalized.contactPhone,
    preferredContactMethod: normalized.preferredContactMethod,
    preferredContactDetails: normalized.preferredContactDetails,
    storeProfile: normalized.storeProfile,
    allowContactSharing: normalized.allowContactSharing,
    artworkCreativeId:
      performer?.artworkCreativeId === '' || performer?.artworkCreativeId == null
        ? null
        : Number(performer.artworkCreativeId),
    inviteChannel: String(performer?.inviteChannel || '').trim(),
    inviteTarget: String(performer?.inviteTarget || '').trim(),
    replacementReason: String(performer?.replacementReason || '').trim(),
  };

  if (includeStatus) {
    payload.status = String(performer?.status || 'awaiting_submission').toLowerCase();
  }

  return payload;
}

function hasPerformerPayload(performerPayload) {
  return (
    !!performerPayload.id ||
    Object.entries(performerPayload).some(([key, value]) => {
      if (key === 'id' || key === 'status') return false;
      if (Array.isArray(value)) return value.length > 0;
      return value != null && value !== '';
    })
  );
}

export function buildGigPayload(form, { includeStatus = false } = {}) {
  const performers = (Array.isArray(form?.performers) ? form.performers : [])
    .filter((performer) => String(performer?.status || '').toLowerCase() !== 'replaced')
    .filter((performer) => String(performer?.status || '').toLowerCase() !== 'cancelled')
    .map((performer) => buildPerformerPayload(performer))
    .filter((performer) => hasPerformerPayload(performer));

  const payload = {
    siteId: Number(form?.siteId) || null,
    title: String(form?.title || '').trim(),
    slug: String(form?.slug || '').trim(),
    description: String(form?.description || '').trim(),
    artworkCreativeId:
      form?.gigArtworkCreativeId === '' || form?.gigArtworkCreativeId == null
        ? null
        : Number(form.gigArtworkCreativeId),
    startsAtUtc: toIsoOrNull(form?.startsAtLocal),
    endsAtUtc: toIsoOrNull(form?.endsAtLocal),
    timezoneName: String(form?.timezoneName || '').trim() || null,
    utcOffsetMinutesSnapshot:
      form?.utcOffsetMinutesSnapshot === '' || form?.utcOffsetMinutesSnapshot == null
        ? null
        : Number(form.utcOffsetMinutesSnapshot),
    highlightPhotos: normalizeGigHighlightPhotos(form?.highlightPhotos || [])
      .map((item, index) => buildHighlightPhotoPayload(item, index)),
    performers,
  };

  if (includeStatus) {
    payload.status = String(form?.status || 'draft').toLowerCase();
  }

  return payload;
}

export function buildGigInvitePayload(performer) {
  return buildPerformerPayload(performer);
}

export function buildSubmissionUrl(submissionToken) {
  if (!submissionToken || typeof window === 'undefined') return null;
  return `${window.location.origin}/gig-submissions/${submissionToken}`;
}

export async function uploadGigHighlightPhotos({ siteId, files = [] } = {}) {
  const resolvedSiteId = Number(siteId || 0);
  const token = getToken();
  if (!resolvedSiteId) {
    throw new Error('A site is required before uploading highlight photos');
  }

  const imageFiles = (Array.isArray(files) ? files : [])
    .filter((file) => file && String(file.type || '').startsWith('image/'));
  if (!imageFiles.length) {
    return [];
  }

  const uploadedItems = [];
  for (const file of imageFiles) {
    const presignResponse = await fetch(
      `/api/uploads/presign?kind=image&filename=${encodeURIComponent(file.name)}&siteId=${encodeURIComponent(resolvedSiteId)}`,
      {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      }
    );
    if (!presignResponse.ok) {
      throw new Error(`Failed to prepare upload (${presignResponse.status})`);
    }

    const { url, key } = await presignResponse.json();
    const putResponse = await fetch(url, {
      method: 'PUT',
      body: file,
      headers: {
        'Content-Type': file.type || 'application/octet-stream',
      },
    });
    if (!putResponse.ok) {
      throw new Error(`Failed to upload highlight photo (${putResponse.status})`);
    }

    const finalizeResponse = await fetch('/api/uploads/finalize', {
      method: 'POST',
      headers: buildHeaders(),
      body: JSON.stringify({
        filename: file.name,
        fileType: file.type,
        key,
        siteId: resolvedSiteId,
        title: buildTitleFromFilename(file.name),
        folder: 'Gig Highlights',
      }),
    });
    if (!finalizeResponse.ok) {
      throw new Error(`Failed to finalize highlight photo (${finalizeResponse.status})`);
    }

    const payload = await finalizeResponse.json();
    const assetId = Number(payload?.asset?.id || 0);
    if (!assetId) {
      throw new Error('Highlight upload did not return an asset id');
    }

    uploadedItems.push({
      id: null,
      creativeId: assetId,
      previewUrl: buildCreativePreviewUrl(assetId),
      sortOrder: uploadedItems.length * 10,
    });
  }

  return uploadedItems;
}

export function normalizeRecurringTemplateItems(items = []) {
  return (Array.isArray(items) ? items : [])
    .filter((item) => item && Number(item.id) > 0)
    .map((item) => ({
      id: Number(item.id),
      siteId: Number(item.siteId || item.site_id || 0) || null,
      name: String(item.name || '').trim() || 'Untitled template',
      defaultDescription: String(item.defaultDescription || item.default_description || '').trim(),
      daysOfWeek: Array.isArray(item.daysOfWeek)
        ? item.daysOfWeek.filter(Boolean).map((entry) => String(entry).trim().toLowerCase())
        : [],
      startTimeLocal: String(item.startTimeLocal || item.start_time_local || '13:00:00'),
      endTimeLocal: String(item.endTimeLocal || item.end_time_local || '16:00:00'),
      timezoneName: String(item.timezoneName || item.timezone_name || 'UTC').trim() || 'UTC',
      utcOffsetMinutesSnapshot:
        item.utcOffsetMinutesSnapshot == null && item.utc_offset_minutes_snapshot == null
          ? 0
          : Number(item.utcOffsetMinutesSnapshot ?? item.utc_offset_minutes_snapshot),
      generateDaysAhead:
        Number(item.generateDaysAhead ?? item.generate_days_ahead) > 0
          ? Number(item.generateDaysAhead ?? item.generate_days_ahead)
          : 90,
      active: item.active == null ? true : toBoolean(item.active, true),
      createdAt: item.createdAt || item.created_at || null,
      updatedAt: item.updatedAt || item.updated_at || null,
    }))
    .sort((a, b) => {
      if (a.active !== b.active) return a.active ? -1 : 1;
      return a.name.localeCompare(b.name) || a.id - b.id;
    });
}

export function createEmptyRecurringTemplateForm({ siteId = null } = {}) {
  const now = new Date();
  return {
    id: null,
    siteId: Number(siteId) || null,
    name: '',
    defaultDescription: '',
    daysOfWeek: ['friday'],
    startTimeLocal: '13:00',
    endTimeLocal: '16:00',
    timezoneName:
      (typeof Intl !== 'undefined' ? Intl.DateTimeFormat().resolvedOptions().timeZone : null) || 'UTC',
    utcOffsetMinutesSnapshot: -now.getTimezoneOffset(),
    generateDaysAhead: 90,
    active: true,
  };
}

export function recurringTemplateToFormState(template) {
  return {
    id: Number(template?.id) || null,
    siteId: Number(template?.siteId || template?.site_id || 0) || null,
    name: template?.name || '',
    defaultDescription: template?.defaultDescription || template?.default_description || '',
    daysOfWeek: Array.isArray(template?.daysOfWeek)
      ? template.daysOfWeek.map((entry) => String(entry).trim().toLowerCase()).filter(Boolean)
      : [],
    startTimeLocal: String(template?.startTimeLocal || template?.start_time_local || '13:00:00').slice(0, 5),
    endTimeLocal: String(template?.endTimeLocal || template?.end_time_local || '16:00:00').slice(0, 5),
    timezoneName: template?.timezoneName || template?.timezone_name || 'UTC',
    utcOffsetMinutesSnapshot:
      template?.utcOffsetMinutesSnapshot == null && template?.utc_offset_minutes_snapshot == null
        ? ''
        : Number(template?.utcOffsetMinutesSnapshot ?? template?.utc_offset_minutes_snapshot),
    generateDaysAhead:
      Number(template?.generateDaysAhead ?? template?.generate_days_ahead) > 0
        ? Number(template?.generateDaysAhead ?? template?.generate_days_ahead)
        : 90,
    active: template?.active == null ? true : toBoolean(template.active, true),
  };
}

export function buildRecurringTemplatePayload(form) {
  const daysOfWeek = Array.from(
    new Set(
      (Array.isArray(form?.daysOfWeek) ? form.daysOfWeek : [])
        .map((entry) => String(entry || '').trim().toLowerCase())
        .filter((entry) => DAY_KEYS.includes(entry))
    )
  );

  return {
    siteId: Number(form?.siteId) || null,
    name: String(form?.name || '').trim(),
    defaultDescription: String(form?.defaultDescription || '').trim(),
    daysOfWeek,
    startTimeLocal: String(form?.startTimeLocal || '').trim(),
    endTimeLocal: String(form?.endTimeLocal || '').trim(),
    timezoneName: String(form?.timezoneName || '').trim() || 'UTC',
    utcOffsetMinutesSnapshot:
      form?.utcOffsetMinutesSnapshot === '' || form?.utcOffsetMinutesSnapshot == null
        ? null
        : Number(form.utcOffsetMinutesSnapshot),
    generateDaysAhead: Number(form?.generateDaysAhead) || 90,
    active: !!form?.active,
  };
}

export async function listGigTimeline({ siteId, from, to }) {
  const params = new URLSearchParams({
    siteId: String(siteId),
    from,
    to,
  });
  const data = await gigApiFetch(`/gigs/timeline?${params.toString()}`);
  return normalizeGigTimelineItems(data.items);
}

export async function listRecurringTemplates({ siteId }) {
  const params = new URLSearchParams({
    siteId: String(siteId),
  });
  const data = await gigApiFetch(`/gigs/templates?${params.toString()}`);
  return normalizeRecurringTemplateItems(data.items);
}

export async function createRecurringTemplate(payload) {
  const data = await gigApiFetch('/gigs/templates', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  return normalizeRecurringTemplateItems([data.template])[0] || null;
}

export async function updateRecurringTemplate(templateId, payload) {
  const data = await gigApiFetch(`/gigs/templates/${templateId}`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
  return normalizeRecurringTemplateItems([data.template])[0] || null;
}

export async function generateRecurringTemplate(templateId) {
  const data = await gigApiFetch(`/gigs/templates/${templateId}/generate`, {
    method: 'POST',
  });
  return data.result;
}

export async function listArtists({ siteId, q = '' }) {
  const params = new URLSearchParams({
    siteId: String(siteId),
  });
  if (String(q || '').trim()) {
    params.set('q', String(q).trim());
  }
  const data = await gigApiFetch(`/artists?${params.toString()}`);
  return normalizeArtistItems(data.items);
}

export async function createArtist(payload) {
  const data = await gigApiFetch('/artists', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  return normalizeArtistItems([data.artist])[0] || null;
}

export async function deleteArtist(artistId) {
  const data = await gigApiFetch(`/artists/${encodeURIComponent(artistId)}`, {
    method: 'DELETE',
  });
  return normalizeArtistItems([data.artist])[0] || null;
}

export async function getGig(gigId) {
  const data = await gigApiFetch(`/gigs/${gigId}`);
  return data.gig;
}

export async function listGigs({ siteId }) {
  const params = new URLSearchParams({
    siteId: String(siteId),
  });
  const data = await gigApiFetch(`/gigs?${params.toString()}`);
  return normalizeGigItems(data.items);
}

export async function createGig(payload) {
  const data = await gigApiFetch('/gigs', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  return data.gig;
}

export async function updateGig(gigId, payload) {
  const data = await gigApiFetch(`/gigs/${gigId}`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
  return data.gig;
}

export async function sendGigInvite(gigId, payload) {
  return gigApiFetch(`/gigs/${gigId}/send-invite`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function replaceGigPerformer(gigId, performerId, payload) {
  return gigApiFetch(`/gigs/${gigId}/performers/${performerId}/replace`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function requestGigChanges(gigId) {
  const data = await gigApiFetch(`/gigs/${gigId}/request-changes`, {
    method: 'POST',
  });
  return data.gig;
}

export async function publishGig(gigId) {
  const data = await gigApiFetch(`/gigs/${gigId}/publish`, {
    method: 'POST',
  });
  return data.gig;
}

export async function cancelGig(gigId) {
  const data = await gigApiFetch(`/gigs/${gigId}/cancel`, {
    method: 'POST',
  });
  return data.gig;
}
